import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Typography,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
} from '@mui/icons-material';
import { graphApi } from '../../../services/api/graph.service';
import { GraphScope, GraphExportOptionsExtended, GraphFilterOptions } from '../../../types/graph.types';

type ExportFormat = 'png' | 'svg' | 'json' | 'graphml' | 'gexf' | 'csv';

interface GraphExportDialogProps {
  open: boolean;
  onClose: () => void;
  scope: GraphScope;
  currentFilters?: GraphFilterOptions;
  nodeCount: number;
  edgeCount: number;
}

/**
 * GraphExportDialog
 *
 * Modal dialog for exporting the knowledge graph with advanced options.
 * Supports format selection, filter application, and metadata inclusion.
 */
const GraphExportDialog: React.FC<GraphExportDialogProps> = ({
  open,
  onClose,
  scope,
  currentFilters,
  nodeCount,
  edgeCount,
}) => {
  const [format, setFormat] = useState<ExportFormat>('json');
  const [applyFilters, setApplyFilters] = useState(true);
  const [includeAnnotations, setIncludeAnnotations] = useState(false);
  const [includeStatistics, setIncludeStatistics] = useState(false);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setError(null);

    try {
      // For PNG/SVG, use client-side Cytoscape export
      if (format === 'png' || format === 'svg') {
        const graphCanvas = (window as any).graphCanvas;
        if (!graphCanvas) {
          setError('Graph canvas not available');
          setExporting(false);
          return;
        }

        let blob: Blob;
        let filename: string;

        if (format === 'png') {
          blob = await graphCanvas.exportPNG();
          filename = `knowledge-graph-${Date.now()}.png`;
        } else {
          const svgData = graphCanvas.exportSVG();
          blob = new Blob([svgData], { type: 'image/svg+xml' });
          filename = `knowledge-graph-${Date.now()}.svg`;
        }

        downloadBlob(blob, filename);
        onClose();
        return;
      }

      // For data formats, use backend API
      const options: GraphExportOptionsExtended = {
        format: format as 'json' | 'graphml' | 'gexf' | 'csv',
        includeStyles: true,
        includeAnnotations,
        includeStatistics,
        includeMetadata,
      };

      if (applyFilters && currentFilters) {
        options.filters = {
          nodeTypes: currentFilters.showNodeTypes
            ? Array.from(currentFilters.showNodeTypes)
            : undefined,
          searchTerm: currentFilters.searchTerm,
          minDegree: currentFilters.minNodeDegree,
        };

        if (currentFilters.dateRange) {
          options.filters.dateRange = {
            start: currentFilters.dateRange.start.toISOString(),
            end: currentFilters.dateRange.end.toISOString(),
          };
        }
      }

      const data = await graphApi.exportGraphExtended(scope, options);

      const mimeTypes: Record<string, string> = {
        json: 'application/json',
        graphml: 'application/xml',
        gexf: 'application/xml',
        csv: 'text/csv',
      };

      const blob = data instanceof Blob
        ? data
        : new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], {
            type: mimeTypes[format] || 'application/octet-stream',
          });

      downloadBlob(blob, `knowledge-graph-${Date.now()}.${format}`);
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Determine which options are available per format
  const isDataFormat = ['json', 'graphml', 'gexf', 'csv'].includes(format);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Knowledge Graph</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Export scope preview */}
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Export scope: <strong>{scope}</strong> view
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {nodeCount} nodes, {edgeCount} edges
            {applyFilters && currentFilters && Object.keys(currentFilters).length > 0
              ? ' (with current filters applied)'
              : ' (all data)'}
          </Typography>
        </div>

        {/* Format selection */}
        <FormControl style={{ marginBottom: 16 }}>
          <FormLabel>Format</FormLabel>
          <RadioGroup
            row
            value={format}
            onChange={(e) => setFormat(e.target.value as ExportFormat)}
          >
            <FormControlLabel value="png" control={<Radio size="small" />} label="PNG" />
            <FormControlLabel value="svg" control={<Radio size="small" />} label="SVG" />
            <FormControlLabel value="json" control={<Radio size="small" />} label="JSON" />
            <FormControlLabel value="graphml" control={<Radio size="small" />} label="GraphML" />
            <FormControlLabel value="gexf" control={<Radio size="small" />} label="GEXF" />
            <FormControlLabel value="csv" control={<Radio size="small" />} label="CSV" />
          </RadioGroup>
        </FormControl>

        <Divider style={{ marginTop: 12, marginBottom: 12 }} />

        {/* Options */}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={applyFilters}
                onChange={(e) => setApplyFilters(e.target.checked)}
                size="small"
                disabled={!isDataFormat}
              />
            }
            label={
              <Typography variant="body2">
                Apply current filters
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAnnotations}
                onChange={(e) => setIncludeAnnotations(e.target.checked)}
                size="small"
                disabled={!isDataFormat}
              />
            }
            label={
              <Typography variant="body2">
                Include annotations
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeStatistics}
                onChange={(e) => setIncludeStatistics(e.target.checked)}
                size="small"
                disabled={!isDataFormat}
              />
            }
            label={
              <Typography variant="body2">
                Include graph statistics
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeMetadata}
                onChange={(e) => setIncludeMetadata(e.target.checked)}
                size="small"
                disabled={!isDataFormat}
              />
            }
            label={
              <Typography variant="body2">
                Include node metadata
              </Typography>
            }
          />
        </FormGroup>

        {!isDataFormat && (
          <Typography variant="caption" color="text.secondary" style={{ marginTop: 8, display: 'block' }}>
            Advanced options are only available for data formats (JSON, GraphML, GEXF, CSV).
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting}
          startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GraphExportDialog;
